import {
  createTheme,
  createStyles,
  MuiThemeProvider,
  withStyles
} from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component, createElement, useState, useEffect } from 'react';
import { Error, Sidebar } from 'react-admin';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import log from 'roarr';

import { SolsticeTheme } from '../themes/SolsticeTheme';
import Menu from './SolsticeMenu';
import Notification from './forked/layout/Notification';
import AppBar from './SolsticeAppBar';
import { useErrorLogger } from '../hooks/useErrorLogger';

const styles = theme =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1,
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      position: 'relative',
      minWidth: 'fit-content',
      width: '100%'
    },
    appFrame: {
      display: 'flex',
      flexDirection: 'row',
      flexGrow: 1,
      minHeight: '100vh'
    },
    contentWithSidebar: {
      display: 'flex',
      flexGrow: 1,
      maxWidth: '100vw'
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      flexBasis: 0,
      padding: 0,
      overflowY: 'auto'
    },
    customHeader: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      overflow: 'hidden'
    }
  });

const sanitizeRestProps = ({
  staticContext,
  history,
  match,
  sidebar,
  ...props
}) => props;

const sideBarStyles = {
  drawerPaper: {
    position: 'static',
    maxHeight: 'calc(100vh - 40px)',
    minWidth: 'fit-content',
    marginTop: 0,
    '& > div': {
      position: 'static'
    }
  }
};
const solsticeSidebar = withStyles(sideBarStyles)(({ classes, ...props }) => (
  <Sidebar {...props} classes={classes} />
));

class CustomLayout extends Component {
  state = { hasError: false, errorMessage: null, errorInfo: null };

  componentDidCatch(errorMessage, errorInfo) {
    // Log the error with "Roarr" => sending the log to the Splunk by calling POST v1/jslogs
    log.error(
      {
        ...errorInfo,
        stacktrace: errorInfo.componentStack,
        tags: 'React Error Boundaries, SWW'
      },
      `${errorMessage}`
    );
    this.setState({ hasError: true, errorMessage, errorInfo });
  }

  render() {
    const {
      appBar,
      children,
      classes,
      className,
      customRoutes,
      error,
      dashboard,
      location,
      logout,
      menu,
      notification,
      open,
      title,
      ...props
    } = this.props;
    const { hasError, errorMessage, errorInfo } = this.state;
    const activated = location.pathname !== '/notactivated';

    return (
      <div
        className={classnames('layout', classes.root, className)}
        {...sanitizeRestProps(props)}
      >
        <div className={classes.appFrame}>
          <main className={classes.contentWithSidebar}>
            {activated &&
              createElement(solsticeSidebar, {
                children: createElement(menu, {
                  logout,
                  hasDashboard: !!dashboard
                })
              })}
            <div className={classes.customHeader}>
              {createElement(appBar, { title, open })}
              <div className={classes.content}>
                {hasError
                  ? createElement(error, {
                      error: errorMessage,
                      errorInfo,
                      title
                    })
                  : children}
              </div>
            </div>
          </main>
          {createElement(notification)}
        </div>
      </div>
    );
  }
}

const componentPropType = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.string
]);

CustomLayout.propTypes = {
  appBar: componentPropType,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  classes: PropTypes.object,
  className: PropTypes.string,
  customRoutes: PropTypes.array,
  dashboard: componentPropType,
  error: componentPropType,
  history: PropTypes.object.isRequired,
  logout: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func,
    PropTypes.string
  ]),
  menu: componentPropType,
  notification: componentPropType,
  open: PropTypes.bool,
  sidebar: componentPropType,
  title: PropTypes.node.isRequired
};

CustomLayout.defaultProps = {
  appBar: AppBar,
  error: Error,
  menu: Menu,
  notification: Notification,
  sidebar: Sidebar
};

const mapStateToProps = state => ({
  open: state.admin.ui.sidebarOpen
});

const EnhancedCustomLayout = connect(
  mapStateToProps,
  {} // Avoid connect passing dispatch in props
)(withRouter(withStyles(styles)(CustomLayout)));

const SolsticeLayout = props => {
  const { theme, ...rest } = props;
  const [muiTheme, setMuiTheme] = useState(() => createTheme(theme));
  // Activate "Roarr" logs to send the app logs to Splunk by calling POST v1/jslogs
  // Catch exceptions thrown within handlers (events exceptions) and log them with "Roarr"
  useErrorLogger();

  useEffect(() => {
    setMuiTheme(createTheme(theme));
  }, [theme]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <EnhancedCustomLayout {...rest} />
    </MuiThemeProvider>
  );
};

SolsticeLayout.propTypes = {
  theme: PropTypes.object
};

SolsticeLayout.defaultProps = {
  theme: SolsticeTheme
};

export default SolsticeLayout;
